import type { Template, TemplateDraft } from '@/types/templateTypes';
import { Client } from '@langchain/langgraph-sdk';
import type {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const LANGGRAPH_BASE_URL =
  (import.meta as any).env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';
const PROCESS_ENDPOINT = `${API_BASE_URL}/templates/import/process`;

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token)
        throw new Error('Failed to acquire token for template generation');
      return token;
    }
    throw error;
  }
};

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: { Authorization: `Bearer ${token}`, 'X-User-Id': userId }
  });

/**
 * Generate a template from a text prompt using AI
 * @param prompt - Natural language description of the desired form/template
 * @param userId - Current user ID
 * @param getAccessTokenSilently - Auth0 token function
 * @param getAccessTokenWithPopup - Auth0 popup token function
 * @param maxQuestions - Maximum number of questions to generate (default: 200)
 * @returns Generated template draft
 */
export const generateTemplateFromPrompt = async (
  prompt: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  maxQuestions = 200
): Promise<TemplateDraft> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const client = createLangGraphClient(LANGGRAPH_BASE_URL, accessToken, userId);
  const thread = await client.threads.create();
  const threadId = thread.thread_id;
  const result = await client.runs.wait(threadId, 'generate_template_from_prompt_graph', {
    input: {
      prompt: prompt,
      max_questions: maxQuestions
    },
    config: {
      configurable: {
        user_id: userId,
        thread_id: threadId,
        prompt: prompt,
        max_questions: maxQuestions
      }
    },
    metadata: {
      user_id: userId,
      thread_id: threadId,
      prompt: prompt,
      max_questions: maxQuestions
    }
  });
  
  // Parse template from message content
  const messages = (result as any)?.messages || [];
  if (!messages.length) {
    throw new Error('No messages returned from template generation');
  }
  
  const lastMessage = messages[messages.length - 1];
  const content = lastMessage?.content || '';
  
  // Check for error
  if (content.startsWith('ERROR:')) {
    throw new Error(content.replace('ERROR: ', ''));
  }
  
  // Parse JSON from message content
  try {
    const parsed = JSON.parse(content);
    return parsed as TemplateDraft;
  } catch (e) {
    throw new Error('Failed to parse template from response: ' + content);
  }
};

/**
 * Create a template from a generated draft
 * @param draft - The generated template draft
 * @param templateCategoryId - Category ID for the template
 * @param userId - Current user ID
 * @param getAccessTokenSilently - Auth0 token function
 * @param getAccessTokenWithPopup - Auth0 popup token function
 * @param templateName - Optional custom name (overrides draft.name)
 * @returns Created template with ID
 */
export const createTemplateFromGenerated = async (
  draft: TemplateDraft,
  templateCategoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  templateName?: string
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const resp = await fetch(PROCESS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify({
      companyDocumentId: null, // No document for prompt-generated templates
      templateCategoryId,
      templateName: templateName || draft.name,
      parsedTemplate: draft
    })
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: resp.statusText }));
    throw new Error(err.message || 'Failed to create template from generated draft');
  }
  return (await resp.json()) as Template;
};

/**
 * Generate a template with streaming updates (for real-time UI feedback)
 * @param prompt - Natural language description of the desired form/template
 * @param userId - Current user ID
 * @param getAccessTokenSilently - Auth0 token function
 * @param getAccessTokenWithPopup - Auth0 popup token function
 * @param onUpdate - Callback for progress updates
 * @param maxQuestions - Maximum number of questions to generate (default: 200)
 * @returns Generated template draft
 */
export const generateTemplateFromPromptWithProgress = async (
  prompt: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  onUpdate: (message: string) => void,
  maxQuestions = 200
): Promise<TemplateDraft> => {
  onUpdate('Initializing template generation...');
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  onUpdate('Connecting to AI service...');
  const client = createLangGraphClient(LANGGRAPH_BASE_URL, accessToken, userId);
  const thread = await client.threads.create();
  const threadId = thread.thread_id;
  
  onUpdate('Generating template structure...');
  const result = await client.runs.wait(threadId, 'generate_template_from_prompt_graph', {
    input: {
      prompt: prompt,
      max_questions: maxQuestions
    },
    config: {
      configurable: {
        user_id: userId,
        thread_id: threadId,
        prompt: prompt,
        max_questions: maxQuestions
      }
    },
    metadata: {
      user_id: userId,
      thread_id: threadId,
      prompt: prompt,
      max_questions: maxQuestions
    }
  });
  
  onUpdate('Validating generated template...');
  const parsed =
    (result as any)?.parsed_template || (result as any)?.data?.parsed_template;
  if (!parsed)
    throw new Error('LangGraph generation did not return parsed_template');
  
  onUpdate('Template generation complete!');
  return parsed as TemplateDraft;
};

/**
 * Example prompts for template generation
 */
export const EXAMPLE_PROMPTS = {
  patientIntake: `Create a comprehensive patient intake form for a medical clinic that collects:
- Personal information (full name, date of birth, address, phone, email)
- Insurance information (provider name, policy number, group number)
- Medical history (current medications, known allergies, past surgeries)
- Emergency contact details
- Consent for treatment`,
  
  employeeOnboarding: `Generate an employee onboarding form including:
- Personal details (name, address, phone, email, SSN)
- Emergency contact information
- Tax withholding information (W-4 details)
- Direct deposit setup
- Benefits enrollment preferences
- Company policy acknowledgment
- IT equipment and access needs`,
  
  customerFeedback: `Create a customer satisfaction survey with:
- Overall satisfaction rating (1-5 scale)
- Product/service quality ratings
- Staff friendliness rating
- Likelihood to recommend (NPS score)
- Areas for improvement (multiple choice and open text)
- Additional comments`,
  
  eventRegistration: `Generate an event registration form that captures:
- Attendee information (name, email, phone, company)
- Ticket type selection
- Dietary restrictions
- Accessibility requirements
- Session preferences
- T-shirt size
- Payment information`,
  
  jobApplication: `Create a job application form including:
- Personal information
- Contact details
- Education history
- Work experience
- Skills and certifications
- References
- Cover letter
- Resume upload
- Availability`,
};

